import {useCallback} from 'react';
import {handleResponse} from '../api/client';
import {useAuth} from './useAuth';
import {busy} from './useBusy';
import {toast} from './useToast';

// Authenticated downloads with progress. A plain <a href> cannot carry the bearer token, so the
// file is fetched, counted as it streams, then handed to the browser: a download link where that
// works, the share sheet in the iOS installed app (where blob downloads just open a preview).

const isStandaloneIos = (): boolean =>
    /iP(hone|ad|od)/.test(navigator.userAgent) && (window.matchMedia('(display-mode: standalone)').matches || (navigator as {standalone?: boolean}).standalone === true);

export const formatBytes = (n: number): string => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

export function useDownload() {
    const {authedFetch} = useAuth();

    return useCallback(
        async (path: string, fileName: string, message: string, expectedBytes?: number): Promise<boolean> => {
            const controller = new AbortController();
            const done = busy.show({message, detail: 'Starting…', onCancel: () => controller.abort()});
            try {
                const response = await authedFetch(path, {signal: controller.signal});
                if (!response.ok) await handleResponse(response);
                const total = Number(response.headers.get('content-length')) || expectedBytes || 0;
                const reader = response.body?.getReader();
                const chunks: BlobPart[] = [];
                let received = 0;
                if (reader) {
                    for (;;) {
                        const {done: finished, value} = await reader.read();
                        if (finished) break;
                        chunks.push(value);
                        received += value.byteLength;
                        busy.update({detail: total ? `${formatBytes(received)} of ${formatBytes(total)}` : formatBytes(received), progress: total ? received / total : undefined});
                    }
                } else {
                    chunks.push(await response.blob());
                }
                const type = response.headers.get('content-type') ?? 'application/octet-stream';
                const file = new File(chunks, fileName, {type});

                if (isStandaloneIos() && navigator.canShare?.({files: [file]})) {
                    done();
                    await navigator.share({files: [file], title: fileName});
                    return true;
                }
                const url = URL.createObjectURL(file);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
                return true;
            } catch (error) {
                if ((error as Error)?.name === 'AbortError') return false;
                toast.problem(error instanceof Error ? error.message : 'The download failed.');
                return false;
            } finally {
                done();
            }
        },
        [authedFetch]
    );
}
